import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React, { useCallback, useEffect, useState } from 'react';

import { OutcomeType } from '~pages/CampaignManagement/domain';
import { useAsteriskDialler } from '~providers/AsteriskDiallerProvider';
import { DiallerAction, SelectOption } from '~providers/AsteriskDiallerProvider/reducer';
import { AgentState, Disposition, UpdateCallDisposition } from '~services/AsteriskDiallerManager/domain';

// Amplifon specific config (App.svelte:857-878)
// TODO: Refactor company-specific hardcoded settings into reusable components to better align with React's core principles.
const clientCodeDispositionCodes = ['successful'];
const clientCodeSubDispositionCodes = [
  'appointment-booked-couple-maint-verbal-renewal',
  'appointment-booked-maint-verbal-renewal',
  'appointment-booked-quad-maint-verbal-renewal',
  'appointment-booked-triple-maint-verbal-renewal',
  'client-review-maintenance-verbal-renewal',
  'maintenance-verbal-renewal',
];
const clientCodeCampaignIds = [
  '1',
  '47',
  '48',
  '49',
  '50',
  '51',
  '52',
  '53',
  '54',
  '55',
  '56',
  '57',
  '78',
  '79',
  '80',
  '83',
];
const hearAboutCampaignIds = [
  '1',
  '10',
  '17',
  '22',
  '45',
  '47',
  '48',
  '49',
  '50',
  '51',
  '52',
  '53',
  '54',
  '55',
  '56',
  '57',
  '58',
  '59',
  '60',
  '61',
  '62',
  '63',
  '64',
  '65',
  '66',
  '67',
  '68',
  '69',
  '71',
  '72',
  '73',
  '74',
  '77',
  '78',
  '79',
  '80',
  '82',
  '83',
  '85',
  '86',
  '87',
  '88',
  '89',
  '90',
  '91',
  '92',
];

// Amplifon dropdown options (App.svelte:880-913)
const hearAboutOptions: SelectOption[] = [
  { label: 'None', value: '' },
  { label: 'TV', value: 'tv' },
  { label: 'Radio', value: 'radio' },
  { label: 'Letter', value: 'letter' },
  { label: 'SMS', value: 'sms' },
  { label: 'Email', value: 'email' },
  { label: 'Word of Mouth', value: 'word-of-mouth' },
  { label: 'Digital', value: 'digital' },
];

// HCF dropdown options (App.svelte:916-963)
const isTransferOptions: SelectOption[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
];
const insuranceOfferedOptions: SelectOption[] = [
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
  { label: 'Authorisation Not Provided', value: 'auth_not_provided' },
];
const insuranceOutcomeOptions: SelectOption[] = [
  { label: 'Not Applicable', value: 'not_applicable' },
  { label: 'Sale', value: 'sale' },
  { label: 'Declined', value: 'declined' },
  { label: 'Information Sent', value: 'information_sent' },
  { label: 'Callback', value: 'callback' },
];

const SectionHeader = styled(Box)({
  backgroundColor: '#F5F5F5',
  padding: '8px 12px',
  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
});

/**
 * Builds and sends setCallDisposition to the server.
 * Called on every field change to keep the server in sync (mirrors Svelte handleDispoSelect).
 */
const buildAndSubmitDispo = (
  dispo: Disposition,
  callbackEndpoint: string,
  callbackToAgent: boolean,
  callbackTime: string | null,
  callbackNotes: string,
  leadEndpoint: string,
  exclusionList: SelectOption | null,
  isNewCustomer: boolean,
  newLeadName: string,
  newLeadTimezone: SelectOption | null,
  newLeadExternalId: string,
  send: (d: UpdateCallDisposition) => void,
): boolean => {
  // Callback validation: time is required
  if (dispo.outcome === OutcomeType.Callback && callbackTime === null) {
    return false;
  }

  // Anonymous callback validation: name, timezone, external ID required (App.svelte:1020-1022)
  if (
    dispo.outcome === OutcomeType.Callback &&
    isNewCustomer &&
    (newLeadTimezone === null || newLeadName === '' || newLeadExternalId === '')
  ) {
    return false;
  }

  // Excluded validation: an exclusion list must be selected
  if (dispo.outcome === OutcomeType.Excluded && exclusionList === null) {
    return false;
  }

  const req: UpdateCallDisposition = {
    code: dispo.code,
    subCode: dispo.subCode,
    callback: [OutcomeType.Callback, OutcomeType.Snoozed].includes(dispo.outcome as OutcomeType)
      ? {
          forAgent: callbackToAgent ? '' : null, // server resolves the current user from the session
          scheduled: callbackTime ?? '',
          scheduledLocalTime: callbackTime ?? '',
          endpoint: callbackEndpoint,
          notes: callbackNotes,
        }
      : null,
    exclusion:
      dispo.outcome === OutcomeType.Excluded && exclusionList
        ? { list: exclusionList.value, endpoint: leadEndpoint }
        : null,
    // Anonymous caller fields (App.svelte:1059-1062)
    ...(isNewCustomer &&
      [OutcomeType.Callback, OutcomeType.Snoozed].includes(dispo.outcome as OutcomeType) && {
        leadName: newLeadName,
        leadTimezone: newLeadTimezone?.value ?? '',
        leadExternalId: newLeadExternalId,
      }),
  };

  send(req);
  return true;
};

const DispositionForm = () => {
  const {
    state: dialler,
    dispatch,
    socketRef,
    loadTimezones,
    hasAmplifonSipFormFields,
    hasHCFSipFormFields,
  } = useAsteriskDialler();
  const [stateMenuAnchor, setStateMenuAnchor] = useState<null | HTMLElement>(null);

  const isNewCustomer = dialler.call?.attemptId === 0;
  const canSubmit = dialler.canDispo && dialler.state === AgentState.AfterCallWork;
  const hasPreviewOrOffered = dialler.pendingPreviewCall !== null || dialler.offeredCall !== null;

  // Amplifon/HCF derived conditions (App.svelte:365-372, 383-385)
  const campaignId = dialler.call?.lead.campaignId ?? '';
  const showClientCode =
    hasAmplifonSipFormFields &&
    dialler.isInbound &&
    clientCodeCampaignIds.includes(campaignId) &&
    clientCodeDispositionCodes.includes(dialler.currentDispo?.code ?? '') &&
    clientCodeSubDispositionCodes.includes(dialler.currentDispo?.subCode ?? '');
  const showHearAboutUs = hasAmplifonSipFormFields && dialler.isInbound && hearAboutCampaignIds.includes(campaignId);
  const trySubmitDispo = useCallback(() => {
    if (!dialler.currentDispo || !socketRef.current) return false;

    // Send HCF/Amplifon attributes before disposition (App.svelte:1065-1128)
    const callId = dialler.call?.id;
    const hasClientCodeField =
      hasAmplifonSipFormFields && dialler.isInbound && clientCodeCampaignIds.includes(campaignId);
    const hasHearAboutField =
      hasAmplifonSipFormFields && dialler.isInbound && hearAboutCampaignIds.includes(campaignId);

    if (
      callId &&
      ((hasAmplifonSipFormFields && dialler.isInbound && (hasClientCodeField || hasHearAboutField)) ||
        hasHCFSipFormFields)
    ) {
      const attributes: { [key: string]: string } = {};

      if (hasHearAboutField && dialler.currentHearAboutUs !== null) {
        attributes.hear_about_us = dialler.currentHearAboutUs.value || '';
      }
      if (
        hasClientCodeField &&
        clientCodeDispositionCodes.includes(dialler.currentDispo.code) &&
        clientCodeSubDispositionCodes.includes(dialler.currentDispo.subCode)
      ) {
        if (dialler.currentClientCode === '') return false;
        attributes.client_code = dialler.currentClientCode;
      }
      if (dialler.currentIsTransfer !== null) {
        attributes.is_transfer = dialler.currentIsTransfer.value || '';
      }
      if (dialler.currentPhoneNumber !== '') {
        attributes.phone_number = dialler.currentPhoneNumber;
      }
      if (dialler.currentMembershipID !== '') {
        attributes.member_id = dialler.currentMembershipID;
      }
      if (dialler.currentInsuranceOffered !== null) {
        attributes.insurance_offered = dialler.currentInsuranceOffered.value || '';
      }
      if (dialler.currentInsuranceOutcome !== null) {
        attributes.insurance_outcome = dialler.currentInsuranceOutcome.value || '';
      }

      socketRef.current.setContactAttributes(callId, attributes);
    }

    return buildAndSubmitDispo(
      dialler.currentDispo,
      dialler.callbackEndpoint,
      dialler.callbackToAgent,
      dialler.callbackTime,
      dialler.callbackNotes,
      dialler.leadEndpoint,
      dialler.currentExclusionList,
      isNewCustomer,
      dialler.newLeadName,
      dialler.newLeadTimezone,
      dialler.newLeadExternalId,
      (d) => socketRef.current?.setCallDisposition(d),
    );
  }, [dialler, socketRef, isNewCustomer, hasAmplifonSipFormFields, hasHCFSipFormFields, campaignId]);

  // Auto-submit to server whenever disposition fields change (mirrors Svelte on:change={handleDispoSelect})
  useEffect(() => {
    trySubmitDispo();
  }, [
    dialler.currentDispo,
    dialler.callbackEndpoint,
    dialler.callbackToAgent,
    dialler.callbackTime,
    dialler.callbackNotes,
    dialler.currentExclusionList,
    dialler.newLeadName,
    dialler.newLeadTimezone,
    dialler.newLeadExternalId,
    dialler.currentClientCode,
    dialler.currentHearAboutUs,
    dialler.currentIsTransfer,
    dialler.currentPhoneNumber,
    dialler.currentMembershipID,
    dialler.currentInsuranceOffered,
    dialler.currentInsuranceOutcome,
  ]);

  // Called when any field changes — update state, then try auto-submit
  const handleDispoSelect = (value: string) => {
    const matched = dialler.dispositions.find((d) => `${d.code}|${d.subCode}` === value) ?? null;
    dispatch({ type: 'SET_CURRENT_DISPO', payload: matched });
    // Fetch timezones for anonymous callback (App.svelte:1013-1014)
    if (matched?.outcome === OutcomeType.Callback && isNewCustomer) {
      loadTimezones();
    }
  };

  const handleFieldChange = (action: DiallerAction) => {
    dispatch(action);
  };

  return (
    <Box sx={{ border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: 1, overflow: 'hidden' }}>
      <SectionHeader>
        <Typography variant='body2' fontWeight={500}>
          Call Disposition
        </Typography>
      </SectionHeader>

      <Box p={1.5}>
        {/* Disposition dropdown */}
        <Autocomplete
          fullWidth
          size='small'
          options={dialler.dispositions}
          value={dialler.currentDispo || null}
          onChange={(_, value) => {
            if (value) {
              handleDispoSelect(`${value.code}|${value.subCode}`);
            } else {
              dispatch({ type: 'SET_CURRENT_DISPO', payload: null });
            }
          }}
          getOptionLabel={(option) => (option.description ? `${option.title} - ${option.description}` : option.title)}
          renderOption={(props, option) => (
            <li {...props} key={`${option.code}|${option.subCode}`}>
              {option.title}
              {option.description && (
                <Typography variant='caption' color='text.secondary' sx={{ ml: 0.5 }}>
                  {` - ${option.description}`}
                </Typography>
              )}
            </li>
          )}
          renderInput={(params) => <TextField {...params} placeholder='Disposition...' />}
          isOptionEqualToValue={(option, value) => option.code === value.code && option.subCode === value.subCode}
        />

        <Box mt={2} display='flex' flexDirection='column' gap={1.5}>
          {/* Callback field — shown when outcome is "callback" */}
          {dialler.currentDispo?.outcome === OutcomeType.Callback && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={dialler.callbackToAgent}
                  onChange={(e) => handleFieldChange({ type: 'SET_CALLBACK_TO_AGENT', payload: e.target.checked })}
                />
              }
              label='Assign to self?'
            />
          )}

          {/* Callback/ Snooze fields — shown when outcome is "callback",  "snoozed" */}
          {[OutcomeType.Callback, OutcomeType.Snoozed].includes(dialler.currentDispo?.outcome as OutcomeType) && (
            <>
              <TextField
                label='Endpoint'
                size='small'
                fullWidth
                value={dialler.callbackEndpoint}
                onChange={(e) => handleFieldChange({ type: 'SET_CALLBACK_ENDPOINT', payload: e.target.value })}
              />

              <TextField
                label='Callback Schedule'
                type='datetime-local'
                size='small'
                fullWidth
                value={dialler.callbackTime ?? ''}
                onChange={(e) => handleFieldChange({ type: 'SET_CALLBACK_TIME', payload: e.target.value || null })}
                helperText={
                  isNewCustomer
                    ? `Local time in ${dialler.newLeadTimezone?.label || 'select timezone'}`
                    : dialler.call
                    ? `Local time in ${dialler.call.lead.timezone}`
                    : undefined
                }
                InputLabelProps={{ shrink: true }}
              />
            </>
          )}

          {/* Callback field — shown when outcome is "callback" */}
          {dialler.currentDispo?.outcome === OutcomeType.Callback && (
            <TextField
              label='Notes'
              size='small'
              fullWidth
              multiline
              rows={3}
              value={dialler.callbackNotes}
              onChange={(e) => handleFieldChange({ type: 'SET_CALLBACK_NOTES', payload: e.target.value })}
            />
          )}

          {/* Anonymous caller fields — shown when customerNum === 0 (App.svelte:313-341) */}
          {[OutcomeType.Callback, OutcomeType.Snoozed].includes(dialler.currentDispo?.outcome as OutcomeType) &&
            isNewCustomer && (
              <>
                <TextField
                  label='Name'
                  size='small'
                  fullWidth
                  value={dialler.newLeadName}
                  onChange={(e) => handleFieldChange({ type: 'SET_NEW_LEAD_NAME', payload: e.target.value })}
                />

                <Autocomplete
                  size='small'
                  options={dialler.timezoneList}
                  getOptionLabel={(opt) => opt.label}
                  value={dialler.newLeadTimezone}
                  onChange={(_, val) => dispatch({ type: 'SET_NEW_LEAD_TIMEZONE', payload: val })}
                  renderInput={(params) => <TextField {...params} label='Timezone' />}
                  isOptionEqualToValue={(opt, val) => opt.value === val.value}
                />

                <TextField
                  label='External ID'
                  size='small'
                  fullWidth
                  value={dialler.newLeadExternalId}
                  onChange={(e) => handleFieldChange({ type: 'SET_NEW_LEAD_EXTERNAL_ID', payload: e.target.value })}
                />
              </>
            )}
        </Box>

        {/* Exclusion list — shown when outcome is "excluded" */}
        {dialler.currentDispo?.outcome === OutcomeType.Excluded && (
          <Box mt={2}>
            <Select
              fullWidth
              size='small'
              value={dialler.currentExclusionList?.value ?? ''}
              onChange={(e) => {
                const matched = dialler.exclusionLists.find((el) => el.value === e.target.value) ?? null;
                dispatch({ type: 'SET_CURRENT_EXCLUSION_LIST', payload: matched });
              }}
              displayEmpty>
              <MenuItem value='' disabled>
                Exclusion Lists...
              </MenuItem>
              {dialler.exclusionLists.map((el) => (
                <MenuItem key={el.value} value={el.value}>
                  {el.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* Amplifon Client Code — App.svelte:365-381 */}
        {showClientCode && (
          <Box mt={2}>
            <Typography variant='body2'>Client Code:</Typography>
            <TextField
              size='small'
              fullWidth
              value={dialler.currentClientCode}
              onChange={(e) => dispatch({ type: 'SET_CURRENT_CLIENT_CODE', payload: e.target.value })}
            />
          </Box>
        )}

        {/* Amplifon "How did you hear about Amplifon?" — App.svelte:383-400 */}
        {showHearAboutUs && (
          <Box mt={2}>
            <Typography variant='body2'>How did you hear about Amplifon?:</Typography>
            <Select
              fullWidth
              size='small'
              value={dialler.currentHearAboutUs?.value ?? ''}
              onChange={(e) => {
                const matched = hearAboutOptions.find((o) => o.value === e.target.value) ?? null;
                dispatch({ type: 'SET_CURRENT_HEAR_ABOUT_US', payload: matched });
              }}
              displayEmpty>
              <MenuItem value='' disabled>
                How did you hear about Amplifon?
              </MenuItem>
              {hearAboutOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* HCF fields — App.svelte:402-461 */}
        {hasHCFSipFormFields && (
          <Box mt={2} display='flex' flexDirection='column' gap={1.5}>
            <Select
              fullWidth
              size='small'
              value={dialler.currentIsTransfer?.value ?? ''}
              onChange={(e) => {
                const matched = isTransferOptions.find((o) => o.value === e.target.value) ?? null;
                dispatch({ type: 'SET_CURRENT_IS_TRANSFER', payload: matched });
              }}
              displayEmpty>
              <MenuItem value='' disabled>
                Did you transfer your member to another department?
              </MenuItem>
              {isTransferOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>

            <TextField
              size='small'
              fullWidth
              placeholder='Phone Number'
              value={dialler.currentPhoneNumber}
              onChange={(e) => dispatch({ type: 'SET_CURRENT_PHONE_NUMBER', payload: e.target.value })}
            />

            <TextField
              size='small'
              fullWidth
              placeholder='Member ID'
              value={dialler.currentMembershipID}
              onChange={(e) => dispatch({ type: 'SET_CURRENT_MEMBERSHIP_ID', payload: e.target.value })}
            />

            <Select
              fullWidth
              size='small'
              value={dialler.currentInsuranceOffered?.value ?? ''}
              onChange={(e) => {
                const matched = insuranceOfferedOptions.find((o) => o.value === e.target.value) ?? null;
                dispatch({ type: 'SET_CURRENT_INSURANCE_OFFERED', payload: matched });
              }}
              displayEmpty>
              <MenuItem value='' disabled>
                Life Insurance Introduced to Member
              </MenuItem>
              {insuranceOfferedOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>

            <Select
              fullWidth
              size='small'
              value={dialler.currentInsuranceOutcome?.value ?? ''}
              onChange={(e) => {
                const matched = insuranceOutcomeOptions.find((o) => o.value === e.target.value) ?? null;
                dispatch({ type: 'SET_CURRENT_INSURANCE_OUTCOME', payload: matched });
              }}
              displayEmpty>
              <MenuItem value='' disabled>
                Outcome
              </MenuItem>
              {insuranceOutcomeOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        )}

        {/* Submit buttons */}
        <Box mt={2} display='flex' flexDirection='column' gap={1}>
          {/* Go Online / View Call */}
          <Button
            fullWidth
            variant='contained'
            disableElevation
            sx={{
              'py': 1.5,
              'textTransform': 'none',
              'color': '#fff',
              'backgroundColor': '#198754',
              ':hover': { backgroundColor: '#157347' },
              '&.Mui-disabled': {
                color: '#fff',
                backgroundColor: '#198754',
                opacity: 0.5,
              },
            }}
            disabled={!canSubmit}
            onClick={() => {
              trySubmitDispo();
              if (hasPreviewOrOffered) {
                socketRef.current?.setState('Preview');
              } else {
                socketRef.current?.setState('Available');
              }
            }}>
            <Box>
              <Typography variant='body2' fontWeight={700} color='inherit' lineHeight={1.2}>
                {hasPreviewOrOffered ? 'View Call' : 'Go Online'}
              </Typography>
              <Typography variant='caption' color='inherit' sx={{ opacity: 0.9 }}>
                {hasPreviewOrOffered
                  ? 'View the next call that has been prepared for you.'
                  : 'System will start calling customers for you.'}
              </Typography>
            </Box>
          </Button>

          {/* Go to State dropdown */}
          <Button
            fullWidth
            variant='contained'
            disableElevation
            sx={{
              'textTransform': 'none',
              'color': '#fff',
              'backgroundColor': '#6c757d',
              ':hover': { backgroundColor: '#5a6268' },
              '&.Mui-disabled': {
                color: '#fff',
                backgroundColor: '#6c757d',
                opacity: 0.5,
              },
            }}
            disabled={!canSubmit}
            onClick={(e) => setStateMenuAnchor(e.currentTarget)}>
            Go to State ▾
          </Button>
          <Menu anchorEl={stateMenuAnchor} open={Boolean(stateMenuAnchor)} onClose={() => setStateMenuAnchor(null)}>
            {dialler.campaignInformation.agentStatuses
              .filter((s) => s !== 'Online')
              .map((status) => (
                <MenuItem
                  key={status}
                  onClick={() => {
                    trySubmitDispo();
                    socketRef.current?.setState(status);
                    setStateMenuAnchor(null);
                  }}>
                  {status}
                </MenuItem>
              ))}
          </Menu>
        </Box>
      </Box>
    </Box>
  );
};

export default DispositionForm;
